import { createContext, useContext, ReactNode } from 'react';
import { SsrPageDef, Hydratable, SsrData } from '../../types/ssrTypes';
import _ from 'lodash';

export interface SsrContextValue {
    ssrData: SsrData;
    isServer?: boolean;
    /**
     * 서버에서 렌더링할 때 SSR 대상 함수를 찾기 위한 렌더링인지 여부
     * 임시 렌더링일 땐 SSR 대상 페이지를 찾는다
     */
    isPreRender?: boolean;
    ssrTargetPages?: {
        key: keyof SsrPageDef;
        params: SsrPageDef[keyof SsrPageDef]['params'];
    }[];
}

const SsrContext = createContext<SsrContextValue | null>(null);

/**
 * SSR 데이터를 주입하기 위한 프로바이더
 */
export const SsrProvider = ({
    children,
    value,
}: {
    children: ReactNode;
    value: SsrContextValue;
}) => {
    return <SsrContext.Provider value={value}>{children}</SsrContext.Provider>;
};

/**
 * 각 컴포넌트에서 viewStore 에 SSR 데이터를 적용하기 위한 훅
 */
export const useSsr = <T extends keyof SsrPageDef>(
    key: T,
    params: SsrPageDef[T]['params'],
    viewStore: Hydratable<T>,
) => {
    const context = useContext(SsrContext);

    if (!context) {
        // SSR이 아니거나 Provider가 없는 경우 - 정상적인 상황
        return;
    }

    // ViewStore가 Hydratable 인터페이스를 구현하지 않은 경우 에러
    if (typeof viewStore?.hydrate !== 'function') {
        throw new Error(
            `ViewStore must extend Hydratable class and implement hydrate() method. ` +
                `ssrPage: ${key}, params: ${JSON.stringify(params)}, ViewStore: ${viewStore?.constructor?.name}`,
        );
    }

    const { isPreRender, ssrTargetPages, ssrData } = context;

    // 임시 렌더링에서 타겟만 찾는 것이라면 타겟까지만 넣고 종료
    if (isPreRender) {
        ssrTargetPages?.push({ key, params });
        return;
    }

    if (!ssrData) {
        return;
    }

    const data = ssrData[key] as SsrPageDef[T]['response'];
    if (!data || _.isEmpty(data)) {
        return;
    }

    // hydrate() 함수는 서버에서 renderToString() 으로 불러올 때와, 클라이언트에서 렌더링 시에 모두 호출될 수 있음
    // 서버에서 호출할 때에는 메모리 이슈 없이 컴포넌트로 데이터를 내려줘야 하고,
    // 클라이언트에서는 mobx 로 구현한 특성 상 viewStore 가 컴포넌트와 분리되어 있음
    // 각 상황을 유지하면서 서버 렌더링을 지원하기 위해,
    // (ssrStore 같은 스토어를 사용하는 대신) 프로바이더로 데이터를 전달하고 렌더링 시점에 useSSr() 로 viewStore 에 주입하는 방식으로 처리함
    viewStore.hydrate(data);

    // 클라이언트에서 렌더링 시에 다시 호출되지 않도록, 한 번 사용한 데이터는 삭제
    if (!context.isServer) {
        delete ssrData?.[key];
    }
};
